//Imports
import React, { useCallback, useEffect, useMemo, useState } from "react";

//Hooks
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

//Actions
import { manageCreateEditManageDriverStatus, reset } from "./redux/slice";

//Controls
import { Button, Form, Input, Modal, Switch } from "@gpstrackit/gtc-react-controls";

//Components
import { NotificationManager, NotificationContainer } from "Components/Notifications";

import { getRequiredTrimmedNonEmptyRules } from "Modules/.common/utils/gtcFormItemRules";

const CreateEditManageDriverStatus = ({ refresh }) => {
  //States
  const [formData, setFormData] = useState({ name: null, accumulateTime: true });

  //Language
  const { messages } = useIntl();

  //Form refs
  const createEditDriverStatusFormRef = Form.useForm();

  //Redux
  const dispatch = useDispatch();
  const {
    driverStatus,
    loading,
    error,
    updateManageDriverStatusResult,
    open,
    drivers
  } = useSelector((state) => state.manageCreateEditManageDriverStatusRedux);

  //Memos
  const title = useMemo(
    () =>
      driverStatus?.name
        ? `${messages["manage_createEditDriverStatus_EditTitle"]} "${driverStatus?.name}"`
        : messages["manage_createEditDriverStatus_CreateTitle"],
    [driverStatus]
  );

  const nameFieldRules = useMemo(
    () => getRequiredTrimmedNonEmptyRules(messages["required_field"]),
    [messages]
  );

  //Handlers and utils
  const handleUpdateManageDriverStatusResult = useCallback(() => {
    const name = formData.name != null ? String(formData.name).trim() : "";
    if (!name) return;
    const { accumulateTime } = formData;
    dispatch(
      manageCreateEditManageDriverStatus({
        name,
        accumulateTime,
        id: driverStatus?.id
      })
    );
  }, [formData, driverStatus]);

  const handleClose = useCallback(() => {
    dispatch(reset());
  }, []);

  const handleSuccess = useCallback(() => {
    NotificationManager.success(
      driverStatus?.id
        ? messages["manage_createEditDriverStatus_success_edit"]
        : messages["manage_createEditDriverStatus_success"],
      messages["success"],
      3000,
      null,
      null,
      "success"
    );
    refresh();
    handleClose();
  }, [driverStatus]);

  useEffect(() => {
    if (!updateManageDriverStatusResult?.id) return;
    handleSuccess();
  }, [updateManageDriverStatusResult]);

  useEffect(() => {
    if (!error) return;
    let errorMessage = error?.error || messages["errorMessage"];
    if (error?.errorData?.errors?.find((err) => err?.errorInfo?.code === 1010)) {
      errorMessage = messages["manage_createEditDriverStatus_duplicatedDriverStatusErrorMessage"];
    }
    NotificationManager.error(errorMessage, messages["error"], 4000, null, null, "error");
    dispatch(reset({ error: null }));
  }, [error]);

  useEffect(() => {
    if (!open || !driverStatus) {
      setTimeout(() => {
        createEditDriverStatusFormRef?.setValues({ accumulateTime: true });
      }, 100);
      return;
    }
    setTimeout(() => {
      const { name, accumulateTime } = driverStatus;
      const defaultData = {
        name,
        accumulateTime
      };
      setFormData(defaultData);
      createEditDriverStatusFormRef.setValues(defaultData);
    });
  }, [driverStatus, open, drivers]);

  useEffect(() => {
    const noEmojisRegex = /[\uD83C-\uDBFF\uDC00-\uDFFF]+/g;
    const value = formData.name;
    if (!value) return;
    if (!noEmojisRegex.test(formData.name)) return;
    const noEmojis = value.replace(noEmojisRegex, "");
    const newFormData = { ...formData, name: noEmojis };
    setFormData(newFormData);
    createEditDriverStatusFormRef.setValues(newFormData);
  }, [formData]);

  return (
    <Modal
      title={title && title.length > 48 ? title.substring(0, 45) + '..."' : title}
      open={open}
      handleClose={handleClose}
      position="right"
      style={{ zIndex: "auto" }}
      size="small"
      loading={loading}
      footer={
        <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
          <Button
            isDisabled={loading}
            style={{ marginRight: 8 }}
            onClick={handleClose}
            variant="secondary"
          >
            {messages["cancel"]}
          </Button>

          <Button
            isDisabled={loading}
            onClick={() => {
              createEditDriverStatusFormRef?.submit();
            }}
            variant="primary"
          >
            {messages["save"]}
          </Button>
        </div>
      }
    >
      <div style={{ padding: 24 }}>
        <Form
          ref={createEditDriverStatusFormRef.ref}
          loading={loading}
          onValidSubmit={() => handleUpdateManageDriverStatusResult()}
          onChangeValues={() => {}}
        >
          <Form.Item
            name="name"
            label={messages["manage_createEditDriverStatus_NameInputLabel"]}
            rules={nameFieldRules}
          >
            <Input
              id="name"
              type="text"
              placeholder={messages["manage_createEditDriverStatus_NameInputLabel"]}
              onChange={(value) => setFormData({ ...formData, name: value })}
              maxLength={20}
              isDisabled={driverStatus?.driverStatusModelId === 2}
            />
          </Form.Item>
          <Form.Item
            name="accumulateTime"
            label={messages["manage_createEditDriverStatus_AccumulateTimeLabel"]}
          >
            <Switch
              id="accumulateTime"
              optionLabels={[messages["yes"], messages["no"]]}
              size={"dynamic"}
              onChange={(value) => setFormData({ ...formData, accumulateTime: value })}
            />
          </Form.Item>
        </Form>
      </div>
      <NotificationContainer />
    </Modal>
  );
};

export default CreateEditManageDriverStatus;
